/*
 * Cart drawer recommendations (custom)
 * - Waits until the cart drawer is open before loading anything (no extra requests on normal page views).
 * - Pulls complementary recommendations for cart products, newest cart item first, a few at a time,
 *   and stops as soon as the slots are filled.
 * - Skips anything already in the cart; shows each product once; max `limit` products.
 * - If nothing qualifies, falls back to the manually selected cross-sell list (filtered in Liquid).
 * - Hides itself if still empty.
 * The theme re-renders the drawer on every cart change, so this element reconnects and recalculates
 * automatically (no page reload). Results are cached per product for the rest of the page view.
 */
(() => {
  if (customElements.get('cart-drawer-recommendations')) return;

  const MAX_SOURCE_PRODUCTS = 10; // never look at more than 10 cart products
  const BATCH_SIZE = 3; // cart products requested at the same time
  const PER_PRODUCT_LIMIT = 10; // Shopify's max; lists are short, extras cover items already in the cart
  const cache = new Map(); // product id -> Promise<[{id, html}]>

  const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const readCards = (root) =>
    Array.from(root.querySelectorAll('[data-cdr-product-id]')).map((el) => ({
      id: el.getAttribute('data-cdr-product-id'),
      html: el.innerHTML,
    }));

  const fetchRecommendations = (productId, sectionId) => {
    const key = productId;
    if (!cache.has(key)) {
      const url = `${routesRoot()}recommendations/products?product_id=${encodeURIComponent(productId)}&limit=${PER_PRODUCT_LIMIT}&intent=complementary&section_id=${encodeURIComponent(sectionId)}`;
      const request = fetch(url, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => readCards(new DOMParser().parseFromString(html, 'text/html')))
        .catch(() => {
          cache.delete(key); // allow a retry on the next cart change
          return [];
        });
      cache.set(key, request);
    }
    return cache.get(key);
  };

  const toElement = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.firstElementChild;
  };

  class CartDrawerRecommendations extends HTMLElement {
    connectedCallback() {
      const run = (this._run = (this._run || 0) + 1);
      const drawer = this.closest('cart-drawer');

      if (!drawer || drawer.hasAttribute('open')) {
        this.update(run);
        return;
      }

      // Drawer closed: wait until the shopper opens it.
      this._observer = new MutationObserver(() => {
        if (!drawer.hasAttribute('open')) return;
        this.stopObserving();
        this.update(run);
      });
      this._observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }

    disconnectedCallback() {
      this._run = (this._run || 0) + 1; // cancels any in-flight update
      this.stopObserving();
    }

    stopObserving() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    async update(run) {
      const limit = parseInt(this.getAttribute('limit'), 10) || 3;
      const sectionId = this.getAttribute('section-id') || 'cart-drawer-recommendations';
      const cartIds = (this.getAttribute('product-ids') || '').split(',').filter(Boolean); // newest first
      const sources = cartIds.slice(0, MAX_SOURCE_PRODUCTS);

      const seen = new Set(cartIds);
      const picked = [];
      const take = (items) => {
        for (const item of items) {
          if (picked.length >= limit) return;
          if (!item.id || seen.has(item.id)) continue;
          seen.add(item.id);
          picked.push(item);
        }
      };

      for (let i = 0; i < sources.length && picked.length < limit; i += BATCH_SIZE) {
        const lists = await Promise.all(
          sources.slice(i, i + BATCH_SIZE).map((id) => fetchRecommendations(id, sectionId))
        );
        if (run !== this._run || !this.isConnected) return; // cart changed while loading
        lists.forEach(take);
      }

      if (picked.length === 0) {
        const fallback = this.querySelector('template[data-cdr-fallback]');
        if (fallback) take(readCards(fallback.content));
      }

      this.render(picked);
    }

    render(items) {
      const header = this.querySelector('[data-cdr-header]');
      const slot = this.querySelector('[data-cdr-list]');
      if (!slot) return;

      slot.replaceChildren();
      header?.querySelector('carousel-navigation')?.remove();

      if (items.length === 0) {
        this.hidden = true;
        return;
      }

      const stack = this.getAttribute('stack') === 'true';
      const carouselId = this.getAttribute('carousel-id') || 'cart-drawer-recommendations-carousel';
      const list = document.createElement(stack ? 'div' : 'scroll-carousel');
      list.className = stack
        ? 'complementary-products__product-list'
        : 'complementary-products__product-list complementary-products__product-list--carousel scroll-area bleed snap-x';
      if (!stack) list.id = carouselId;

      for (const item of items) {
        const card = toElement(item.html);
        if (card) list.appendChild(card);
      }

      slot.appendChild(list);
      this.hidden = false;

      if (!header) return;

      const showDots = !stack && items.length > 1;
      if (showDots) {
        const dotLabel = header.getAttribute('data-dot-label') || '__index__';
        const nav = document.createElement('carousel-navigation');
        nav.setAttribute('aria-controls', carouselId);
        nav.className = 'page-dots page-dots--narrow';
        items.forEach((_, index) => {
          const button = document.createElement('button');
          button.className = 'relative';
          button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
          const label = document.createElement('span');
          label.className = 'sr-only';
          label.textContent = dotLabel.replace('__index__', String(index + 1));
          button.appendChild(label);
          nav.appendChild(button);
        });
        header.appendChild(nav);
      }
      header.hidden = !(header.hasAttribute('data-has-title') || showDots);
    }
  }

  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
})();
